// D4 방향 c 의 선 아이콘 한 벌. 새 의존성 없이 손으로 그렸다.
//
// 규칙 넷.
// 1. 모두 viewBox 24, 선 굵기 2, 둥근 끝, 채움 없음, 색은 글자색(currentColor)을 따른다. 그래서 아이콘의 색은 늘 그 자리 글자나
//    아이콘 타일의 범주색과 같고, 팔레트 밖 색이 아이콘으로 새어 들어오지 않는다.
// 2. 모두 장식이다(aria-hidden). 버튼과 탭의 이름은 원래 글자가 그대로 진다. 아이콘만 있는 버튼은 만들지 않는다.
// 3. 카운트다운 숫자 곁과 상단 고정줄의 남은 시간 곁에는 두지 않는다(헌법: 장식이 숫자 인지를 이기면 실패다).
// 4. 이모지와 색 그림은 쓰지 않는다. 기기마다 모양이 다르고 팔레트 밖 색을 끌어온다.

import type React from 'react'

export type IconName =
  | 'mark'
  | 'clock'
  | 'users'
  | 'roster'
  | 'attack'
  | 'defense'
  | 'speaker'
  | 'speaker-off'
  | 'listen'
  | 'eye'
  | 'layers'
  | 'plus'
  | 'send'
  | 'check'
  | 'retime'
  | 'key'
  | 'leave'
  | 'flag'
  | 'edit'
  | 'user'

const PATHS: Record<IconName, React.ReactNode> = {
  // 제품 표지. 표적(착탄 지점) 하나에 떨어지는 유성 하나. rally 의 착탄을 맞추는 도구라는 뜻이다.
  // c2 에서 다시 그렸다. c 의 「가는 선 셋 + 작은 원」은 24px 와 16px 에서 낙서처럼 뭉개졌다(두 비평). 지금은 큰 고리 하나와 가운데 점, 고리에서
  // 떨어진 채운 머리와 짧은 쐐기꼴 꼬리의 유성 하나뿐이라 16px 에서도 「표적」과 「거기로 날아오는 것」 두 덩어리로 읽힌다.
  mark: (
    <>
      <circle cx="15.5" cy="15.5" r="6.5" />
      <circle cx="15.5" cy="15.5" r="2" fill="currentColor" stroke="none" />
      <circle cx="6" cy="6" r="2.9" fill="currentColor" stroke="none" />
      <path d="M1.2 1.2L5.6 3.1L3.1 5.6Z" fill="currentColor" stroke="currentColor" strokeWidth={1.2} />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8.5" r="3.5" />
      <path d="M2.5 20c.6-3.4 3.2-5.5 6.5-5.5s5.9 2.1 6.5 5.5" />
      <path d="M15.5 5.2a3.5 3.5 0 0 1 0 6.6M18 14.8c2 .7 3.2 2.6 3.5 5.2" />
    </>
  ),
  roster: (
    <>
      <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
      <circle cx="9" cy="10.5" r="2.2" />
      <path d="M5.8 16.5c.6-1.6 1.8-2.4 3.2-2.4s2.6.8 3.2 2.4M14.5 10h3.5M14.5 14h3.5" />
    </>
  ),
  attack: (
    <>
      <circle cx="12" cy="12" r="7.5" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <path d="M12 2v4.5M12 17.5V22M2 12h4.5M17.5 12H22" />
    </>
  ),
  defense: <path d="M12 3l7.5 3v5.5c0 4.4-3.1 7.9-7.5 9.5-4.4-1.6-7.5-5.1-7.5-9.5V6L12 3z" />,
  speaker: (
    <>
      <path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z" />
      <path d="M15.5 9a4.2 4.2 0 0 1 0 6M18 6.5a7.8 7.8 0 0 1 0 11" />
    </>
  ),
  'speaker-off': (
    <>
      <path d="M4 9.5h3.5L12 5.5v13l-4.5-4H4z" />
      <path d="M16 9.5l5 5M21 9.5l-5 5" />
    </>
  ),
  listen: (
    <>
      <path d="M4 15v-3a8 8 0 0 1 16 0v3" />
      <rect x="3" y="14" width="4.5" height="6.5" rx="1.6" />
      <rect x="16.5" y="14" width="4.5" height="6.5" rx="1.6" />
    </>
  ),
  eye: (
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="2.8" />
    </>
  ),
  layers: (
    <>
      <path d="M12 3.5l8.5 4.5-8.5 4.5L3.5 8 12 3.5z" />
      <path d="M3.5 12.5l8.5 4.5 8.5-4.5M3.5 16.5L12 21l8.5-4.5" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  send: <path d="M4 11.5L20 4l-6.5 16-2.6-6.6L4 11.5zM10.9 13.4L20 4" />,
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  retime: (
    <>
      <path d="M4.5 12a7.5 7.5 0 1 0 2.4-5.5" />
      <path d="M4.5 4v4h4M12 8.5V12l2.5 1.5" />
    </>
  ),
  key: (
    <>
      <circle cx="8" cy="15" r="4" />
      <path d="M11 12l8.5-8.5M16.5 6.5l3 3M14 9l2 2" />
    </>
  ),
  leave: <path d="M14 4.5h3.5a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H14M10 16.5L5.5 12 10 7.5M5.5 12H15" />,
  flag: <path d="M5.5 21V4M5.5 4.5h11.5l-2.5 4 2.5 4H5.5" />,
  edit: <path d="M4.5 19.5h4l10-10-4-4-10 10v4zM13 7l4 4" />,
  // c2: 사람 한 명(수비 열람의 아군 묶음 머리).
  user: (
    <>
      <circle cx="12" cy="8.5" r="3.75" />
      <path d="M4.5 20c.8-3.8 3.7-6 7.5-6s6.7 2.2 7.5 6" />
    </>
  ),
}

export function Icon(props: { name: IconName; size?: number; className?: string }): React.ReactElement {
  const size = props.size ?? 16
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`icon shrink-0 ${props.className ?? ''}`}
    >
      {PATHS[props.name]}
    </svg>
  )
}

/**
 * 아이콘 타일. 둥근 사각 안에 범주색 아이콘 하나. 구획 머리와 페이지 머리에만 둔다.
 *
 * 범주는 넷이다: `accent`(attack 과 지금 만드는 것, 오렌지), `cyan`(defense), `neutral`(명부와 관리), `kind-*`(rally 타입).
 * 색은 CSS 의 `.tile-*` 가 정한다(면 12%, 테두리 35%, 아이콘 100%).
 */
export function IconTile(props: {
  name: IconName
  tone?: 'accent' | 'cyan' | 'neutral'
  size?: 'sm' | 'md' | 'lg'
  className?: string
}): React.ReactElement {
  const size = props.size ?? 'md'
  const px = size === 'lg' ? 20 : size === 'sm' ? 14 : 16
  return (
    <span aria-hidden="true" className={`tile tile-${size} tile-${props.tone ?? 'neutral'} ${props.className ?? ''}`}>
      <Icon name={props.name} size={px} />
    </span>
  )
}

/**
 * 제품 이름 글자. 「ops」 낱말만 오렌지로 칠한다(글자는 그대로, 감싸는 span 만 더한다). 「ops」가 없는 문자열은 그대로 그린다.
 */
export function Wordmark(props: { text: string }): React.ReactElement {
  const parts = props.text.split(/(ops)/)
  return (
    <>
      {parts.map((part, index) =>
        part === 'ops' ? (
          <span key={index} className="text-accent">
            {part}
          </span>
        ) : (
          part
        ),
      )}
    </>
  )
}
